import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  buildAccountPayload,
  createConstraintRow,
  createInitialFormValues,
  normalizeAccountConstraints
} from '../helpers';
import type { AccountModel, AccountModelInput, TaiKhoan } from '@/types/schemas';
import { FormSchema, type FormValues } from '../schemas';
import type { AccountConstraintRow } from '../types';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

type FormMode = 'add' | 'edit' | 'view';

interface AccountFormProviderProps extends PropsWithChildren {
  account: AccountModel | null;
  mode: FormMode;
  isCopyMode: boolean;
  onClose: () => void;
  onSuccess: (account?: AccountModel) => Promise<void>;
}

interface AccountFormContextValue {
  mode: FormMode;
  initialValues: FormValues;
  parentAccount: TaiKhoan | null;
  setParentAccount: (account: TaiKhoan | null) => void;
  constraints: AccountConstraintRow[];
  selectedConstraintRowId: string | null;
  selectedConstraintCell: { id: string; field: string } | null;
  isDirty: boolean;
  isCloseConfirmOpen: boolean;
  isSubmitting: boolean;
  markDirty: () => void;
  handleSubmit: (values: FormValues) => Promise<void>;
  handleRequestClose: () => void;
  handleCloseConfirm: () => void;
  handleConfirmClose: () => void;
  handleConstraintRowClick: (params: { id: string; row: AccountConstraintRow }) => void;
  handleConstraintCellClick: (params: { id: string; field: string; row: AccountConstraintRow }) => void;
  handleConstraintValueChange: (rowId: string, field: keyof AccountConstraintRow, value: unknown) => void;
  handleAddConstraintRow: () => void;
  handleDeleteConstraintRow: () => void;
  handleCopyConstraintRow: () => void;
  handlePasteConstraintRow: () => void;
  handleMoveConstraintRow: (direction: 'up' | 'down') => void;
}

const AccountFormContext = createContext<AccountFormContextValue | null>(null);

export function AccountFormProvider({
  children,
  account,
  mode,
  isCopyMode,
  onClose,
  onSuccess
}: AccountFormProviderProps) {
  const { addItem: addAccount, updateItem: updateAccount } = useCRUD<AccountModel, AccountModelInput>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    initialFetch: false
  });

  const [parentAccount, setParentAccountState] = useState<TaiKhoan | null>(account?.parent_account_code_data ?? null);
  const [constraints, setConstraints] = useState<AccountConstraintRow[]>([]);
  const [selectedConstraintRowId, setSelectedConstraintRowId] = useState<string | null>(null);
  const [selectedConstraintCell, setSelectedConstraintCell] = useState<{ id: string; field: string } | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = useMemo(() => createInitialFormValues(account, isCopyMode), [account, isCopyMode]);

  useEffect(() => {
    const normalizedFromDetail = normalizeAccountConstraints(account?.constraints);

    setParentAccountState(account?.parent_account_code_data ?? null);
    const nextRows = isCopyMode
      ? normalizedFromDetail.rows.map(row =>
          createConstraintRow({
            ...row,
            id: undefined,
            uuid: undefined,
            isNew: Boolean(row.uuid)
          })
        )
      : normalizedFromDetail.rows;

    setConstraints(nextRows);
    setSelectedConstraintRowId(null);
    setSelectedConstraintCell(null);
    setIsDirty(false);
    setIsCloseConfirmOpen(false);
  }, [account, isCopyMode]);

  const markDirty = useCallback(() => {
    if (mode !== 'view') {
      setIsDirty(true);
    }
  }, [mode]);

  const setParentAccount = useCallback(
    (nextAccount: TaiKhoan | null) => {
      setParentAccountState(nextAccount);
      markDirty();
    },
    [markDirty]
  );

  const updateConstraints = useCallback(
    (updater: (rows: AccountConstraintRow[]) => AccountConstraintRow[]) => {
      setConstraints(previousRows => updater(previousRows));
      markDirty();
    },
    [markDirty]
  );

  const handleConstraintRowClick = useCallback((params: { id: string; row: AccountConstraintRow }) => {
    setSelectedConstraintRowId(params.id);
  }, []);

  const handleConstraintCellClick = useCallback((params: { id: string; field: string; row: AccountConstraintRow }) => {
    setSelectedConstraintRowId(params.id);
    setSelectedConstraintCell({ id: params.id, field: params.field });
  }, []);

  const handleConstraintValueChange = useCallback(
    (rowId: string, field: keyof AccountConstraintRow, value: unknown) => {
      const currentRow = constraints.find(row => row.id === rowId);
      if (!currentRow) return;

      const nextValue = field === 'requireInput' ? Boolean(value) : String(value ?? '');
      const nextRow = createConstraintRow({
        ...currentRow,
        [field]: nextValue
      });

      updateConstraints(rows => rows.map(row => (row.id === rowId ? nextRow : row)));
    },
    [constraints, updateConstraints]
  );

  const handleAddConstraintRow = useCallback(() => {
    const nextRow = createConstraintRow({ objectName: '', requireInput: false, isNew: true });
    updateConstraints(rows => [...rows, nextRow]);
    setSelectedConstraintRowId(nextRow.id);
  }, [updateConstraints]);

  const handleDeleteConstraintRow = useCallback(() => {
    if (!selectedConstraintRowId) return;

    updateConstraints(rows => rows.filter(row => row.id !== selectedConstraintRowId));
    setSelectedConstraintRowId(null);
    setSelectedConstraintCell(null);
  }, [selectedConstraintRowId, updateConstraints]);

  const handleCopyConstraintRow = useCallback(() => {
    if (!selectedConstraintRowId) return;

    updateConstraints(rows => {
      const selectedRow = rows.find(row => row.id === selectedConstraintRowId);
      if (!selectedRow) return rows;

      return [
        ...rows,
        createConstraintRow({
          objectName: selectedRow.objectName,
          requireInput: selectedRow.requireInput,
          isNew: true
        })
      ];
    });
  }, [selectedConstraintRowId, updateConstraints]);

  const handlePasteConstraintRow = useCallback(() => {
    void handleCopyConstraintRow();
  }, [handleCopyConstraintRow]);

  const handleMoveConstraintRow = useCallback(
    (direction: 'up' | 'down') => {
      if (!selectedConstraintRowId) return;

      updateConstraints(rows => {
        const currentIndex = rows.findIndex(row => row.id === selectedConstraintRowId);
        if (currentIndex === -1) return rows;

        const targetIndex =
          direction === 'up' ? Math.max(currentIndex - 1, 0) : Math.min(currentIndex + 1, rows.length - 1);

        if (targetIndex === currentIndex) {
          return rows;
        }

        const nextRows = [...rows];
        const [movedRow] = nextRows.splice(currentIndex, 1);
        nextRows.splice(targetIndex, 0, movedRow);
        return nextRows;
      });
    },
    [selectedConstraintRowId, updateConstraints]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      setIsSubmitting(true);

      try {
        FormSchema.parse(values);
        const payload = buildAccountPayload(values, parentAccount, constraints, isCopyMode ? undefined : account?.uuid);
        const savedAccount =
          mode === 'edit' && account?.uuid ? await updateAccount(account.uuid, payload) : await addAccount(payload);

        setIsDirty(false);
        await onSuccess(savedAccount);
      } finally {
        setIsSubmitting(false);
      }
    },
    [account?.uuid, addAccount, constraints, isCopyMode, mode, onSuccess, parentAccount, updateAccount]
  );

  const handleRequestClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCloseConfirm = useCallback(() => {
    setIsCloseConfirmOpen(false);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setIsCloseConfirmOpen(false);
    setIsDirty(false);
    onClose();
  }, [onClose]);

  const value = useMemo<AccountFormContextValue>(
    () => ({
      mode,
      initialValues,
      parentAccount,
      setParentAccount,
      constraints,
      selectedConstraintRowId,
      selectedConstraintCell,
      isDirty,
      isCloseConfirmOpen,
      isSubmitting,
      markDirty,
      handleSubmit,
      handleRequestClose,
      handleCloseConfirm,
      handleConfirmClose,
      handleConstraintRowClick,
      handleConstraintCellClick,
      handleConstraintValueChange,
      handleAddConstraintRow,
      handleDeleteConstraintRow,
      handleCopyConstraintRow,
      handlePasteConstraintRow,
      handleMoveConstraintRow
    }),
    [
      constraints,
      handleAddConstraintRow,
      handleCloseConfirm,
      handleConfirmClose,
      handleConstraintCellClick,
      handleConstraintRowClick,
      handleConstraintValueChange,
      handleCopyConstraintRow,
      handleDeleteConstraintRow,
      handleMoveConstraintRow,
      handlePasteConstraintRow,
      handleRequestClose,
      handleSubmit,
      initialValues,
      isCloseConfirmOpen,
      isDirty,
      isSubmitting,
      markDirty,
      mode,
      parentAccount,
      selectedConstraintCell,
      selectedConstraintRowId,
      setParentAccount
    ]
  );

  return <AccountFormContext.Provider value={value}>{children}</AccountFormContext.Provider>;
}

export const useAccountFormContext = () => {
  const context = useContext(AccountFormContext);
  if (!context) {
    throw new Error('useAccountFormContext must be used within AccountFormProvider');
  }

  return context;
};
